import { createAsyncThunk, createEntityAdapter, createSlice, EntityState } from "@reduxjs/toolkit";
import { AppDataState, AppId, AppThunkAPIType, unknownError } from "../../app/appTypes";
import { isSameRequestId, load } from "../../app/crud";
import { RootState } from "../../app/store";
import { getWithAuth, postWithAuth, putWithAuth } from "../../http";
import { logout } from "../user/userSlice";
import { ObjectValidationStatus } from "../project/validationProcess/validationProcessApi";
import { ReactNode } from "react";
import { StatusLevelAlertLevel, StatusLevelAlertStatus } from "../../app/components/StatusLevelAlert";

export interface Device {
    id: AppId,
    name: string,
    structureId: AppId,
    model: string,
    unknownNo: boolean,
    serialNo: string,
    productionYear: number,
    description: string
    warrantyStatus: string
    manufacturer: string,
    supplierId: string,
    supplierName: string,
    isGxpRelevant: boolean
    validationStatus: ObjectValidationStatus | null
    guid: string
}

export interface DeviceDescriptionPayload {
    fieldName: string
    text: ReactNode
}

const adapter = createEntityAdapter<Device>({
    selectId: (device) => device.id,
})

export type DraftDevice = Omit<Device, "id">

export type DevicesState = EntityState<Device> & { state: AppDataState, moduleId: AppId | undefined }

const initialState: DevicesState = adapter.getInitialState({
    state: { type: "empty" },
    moduleId: undefined,
})

export const loadDeviceDetails = createAsyncThunk<Device, string, AppThunkAPIType>("devices/load-details",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/devices/${id}` }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as Device
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadDevicesForSupplier = createAsyncThunk<Device[], { moduleId: string, supplierId: string }, AppThunkAPIType>("devices/load-for-supplier",
    async ({ moduleId, supplierId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/devices?moduleId=${moduleId}&supplierId=${supplierId}` }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as Device[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadDevices = load<AppId, Device>(
    "devices/load",
    moduleId => `api/devices?moduleId=${moduleId}`,
    state => selectDevices(state).state,
)

export const createDevice = createAsyncThunk<Device, DraftDevice, AppThunkAPIType>(
    "devices/create",
    async (device: DraftDevice, { rejectWithValue, dispatch }) => {
        const result = await dispatch(postWithAuth({
            url: "api/devices",
            payload: device,
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as Device
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateDevice = createAsyncThunk<Device, Device, AppThunkAPIType>(
    "devices/update",
    async (device: Device, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/devices/${device.id}`,
            payload: device,
        }))
        const { payload } = result

        if (putWithAuth.fulfilled.match(result)) {
            return device
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })


export const updateDeviceDescription = createAsyncThunk<Device, { deviceId: number, data: DeviceDescriptionPayload }, AppThunkAPIType>(
    "devices/update-description",
    async ({ deviceId, data }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/devices/${deviceId}/description`,
            payload: data,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as Device
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateDeviceSerialNo = createAsyncThunk<Device, { deviceId: number, unknownNo: boolean, serialNo: string }, AppThunkAPIType>(
    "devices/update-serial-no",
    async ({ deviceId, unknownNo, serialNo }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/devices/${deviceId}/serial-no`,
            payload: { unknownNo, serialNo },
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as Device
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export interface CheckSerialNoPayload {
    deviceId?: number;
    serialNo: string;
    structureId: number;
    supplierId: number;
}

export interface CheckSerialNoResponse {
    status: StatusLevelAlertStatus;
    level: StatusLevelAlertLevel;
}

export const checkSerialNo = createAsyncThunk<CheckSerialNoResponse, CheckSerialNoPayload, AppThunkAPIType>(
    "devices/check-serial-no",
    async (data, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/devices/exists`,
            payload: data,
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as CheckSerialNoResponse
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const deleteDevice = createAsyncThunk("devices/delete", async (id: AppId) => {
    return id;
})


export interface DeviceExistsResponse {
    exists: boolean
}

export const deviceExists = createAsyncThunk<DeviceExistsResponse, string, AppThunkAPIType>(
    "document/exists", async (serialNo, { dispatch, rejectWithValue }) => {
        const rawUrl = `api/devices/exists?serialNo=${serialNo}`
        const result = await dispatch(getWithAuth({
            url: encodeURI(rawUrl),
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DeviceExistsResponse
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)


export const deviceSlice = createSlice({
    name: "devices",
    initialState,
    reducers: {
        clearDeviceSlice: (state) => {
            adapter.removeAll(state)
            state.state = { type: "empty" }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(createDevice.fulfilled, (state, action) => {
            state = adapter.addOne(state, action.payload)
        })
        builder.addCase(deleteDevice.fulfilled, (state, action) => {
            state = adapter.removeOne(state, action.payload)
        })
        builder.addCase(updateDevice.fulfilled, (state, action) => {
            const { id } = action.payload
            const changes: Omit<Device, "id"> = action.payload
            state = adapter.updateOne(state, {
                id,
                changes,
            });
        })
        builder.addCase(updateDeviceDescription.fulfilled, (state, action) => {
            const { id } = action.payload
            const changes: Omit<Device, "id"> = action.payload
            state = adapter.updateOne(state, {
                id,
                changes,
            });
        })
        builder.addCase(updateDeviceSerialNo.fulfilled, (state, action) => {
            const { id } = action.payload
            const changes: Omit<Device, "id"> = action.payload
            state = adapter.updateOne(state, {
                id,
                changes,
            });
        })
        builder.addCase(loadDevices.rejected, (state, action) => {
            if (isSameRequestId(state.state, action.meta.requestId)) {
                state.state = {
                    type: "error",
                    error: action.payload ?? unknownError(),
                }
            }
        })
        builder.addCase(loadDevices.pending, (state, action) => {
            if (state.state.type === "empty") {
                state.state = {
                    type: "loading",
                    requestId: action.meta.requestId,
                }
            }
        })
        builder.addCase(loadDevices.fulfilled, (state, action) => {
            if (isSameRequestId(state.state, action.meta.requestId)) {
                adapter.setAll(state, action.payload)
                state.state = { type: "loaded" }
                state.moduleId = action.meta.arg
            }
        })
        builder.addCase(loadDeviceDetails.fulfilled, (state, action) => {
            adapter.setOne(state, action.payload)
        })
        builder.addCase(logout.fulfilled, state => {
            adapter.removeAll(state)
            state.state = { type: "empty" }
        })
    },
})

export const { clearDeviceSlice } = deviceSlice.actions

export const selectDevices = (state: RootState): DevicesState => state.devices

export const
    { selectAll: selectAllDevices
        , selectById: selectDeviceById
        , selectTotal: selectTotalDevices
    } = adapter.getSelectors<RootState>(selectDevices)

export default deviceSlice.reducer;
